import { Injectable } from "@angular/core";
import { CVApiService } from "../reusables/services/cv-api.service";
import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import { AddEditArgs, Broadcast, BroadcastApiModel, BroadcastRecipient, IframeApiModel, ReadReceiptEntry, RecipientAssignment } from "src/shared/models/broadcasts.model";
import { ApplicationDTO } from "../forms/forms.model";

@Injectable({
    providedIn: "root",
})
export class WorkAidsService {

    private modes = new BehaviorSubject<AddEditArgs>({} as any)
    currentModes = this.modes.asObservable();
    public progressPopupOpen: boolean = false;
    public folders: any = null;
    public workAidList: any = [];
    public allTags: any = [];
    public loadingRecipients: boolean = false;
    public quizzes: ApplicationDTO[];
    public quizPreviewEditable:boolean = false;
    public quizPreviewShowScore:boolean = true;
    public score:number = null;
    public recordingId: number = 0; 
    public quizPopupOpen: boolean = false;

    public save: Broadcast = {
        BroadcastType: 0,
        BroadcastID: null,
        Title: "",
        BroadcastText: "",
        Folder: "",
        Urgent: false,
        ExpirationDate: null, 
        Quiz: null,
        OriginalQuiz: null,
        Recipients: [],
        RecipientAssignments: [],
        Attachments: [],
        Images: [],
        ImagesData: [],
        Hyperlinks: [],
        Videos: [],
        VideoInfo: [],
        Tags: null,
        MinorEdit: false,
    };

    constructor(private cvApiService: CVApiService) {}

    public async loadWorkAids(): Promise<BroadcastApiModel> {
        try {
            const response = await this.cvApiService.get<BroadcastApiModel>(
                `/api/v1/broadcasts/0`
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed getting work aids.");
            throw err;
        }
    }

    public async GetReadReceipt(workAidId): Promise<ReadReceiptEntry[]> {
        try {
            const response = await this.cvApiService.get<ReadReceiptEntry[]>(
                "/api/v1/broadcast/" + workAidId + "/read_receipt"
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed getting work aid read receipt.");
            throw err;
        }
    }

    public async ExportReadReceipt(workAidId): Promise<string> {
        try {
            const response = await this.cvApiService.get<string>(
                "/api/v1/broadcast/" + workAidId + "/export"
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed exporting work aid read receipt.");
            throw err;
        }
    }

    public async ResendNotification(workAidId){
        try {
            const response = await this.cvApiService.put(
                "/api/v1/broadcast/" + workAidId + "/resend_notification"
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to resend work aid notification.");
            throw err;
        }
    }

    public async ArchiveWorkAid(workAidId)
    {
        try {
            const response = await this.cvApiService.delete(
                "/api/v1/broadcast/" + workAidId + "/archive"
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to archive work aid.");
            throw err;
        }
    }

    public async DeleteWorkAid(workAidId)
    {
        try {
            const response = await this.cvApiService.delete(
                "/api/v1/broadcast/" + workAidId
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to delete work aid.");
            throw err;
        }
    }

    async AddNewWorkAid() {
        const recipientsList = [];
        this.save.RecipientAssignments.forEach((recipient)=> {
            recipient.roles.forEach(role => {
            const recipientObj: BroadcastRecipient ={
                BroadcastID: this.save.BroadcastID,
                CompanyID: role.CompanyID,
                GroupLevel: recipient.group.GroupLevel,
                GroupID: recipient.group.GroupID,
                GroupType: 'E',
                RoleID: role.ProfileID
            }
                recipientsList.push(recipientObj);
            })
        })

        this.save.Recipients = recipientsList;
        this.save.BroadcastType = 0;
        this.save.AnnouncementType = 0;

        try {
            const response = await this.cvApiService.post(
                "/api/v1/broadcast/", this.save
            );

            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        } catch (err) {
            console.error("Failed to add new work aid.");
            throw err;
        }
    }

    async EditWorkAid() {
        const recipientsList = [];
        this.save.RecipientAssignments.forEach((recipient)=> {
            recipient.roles.forEach(role => {
            const recipientObj: BroadcastRecipient ={
                BroadcastID: this.save.BroadcastID,
                CompanyID: role.CompanyID,
                GroupLevel: recipient.group.GroupLevel,
                GroupID: recipient.group.GroupID,
                GroupType: 'E',
                RoleID: role.ProfileID
            }
                recipientsList.push(recipientObj);
            })
        })

        this.save.Recipients = recipientsList;
        this.save.BroadcastType = 0;

        try {
            const response = await this.cvApiService.put(
                "/api/v1/broadcast/" , this.save
            );

            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        } catch (err) {
            console.error("Failed to edit work aid.");
            throw err;
        }
    }

    public async GetRecipientAssignments(workAidId): Promise<RecipientAssignment[]>{
        try {
            const response = await this.cvApiService.get<RecipientAssignment[]>(
                "/api/v1/broadcast/" + workAidId +  "/recipient_assignments"
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to resend work aid notification.");
            throw err;
        }
    }

    public async GetAccessibleQuizzes(): Promise<ApplicationDTO[]>{
        try {
            const response = await this.cvApiService.get<ApplicationDTO[]>(
                "/api/v1/broadcast/accessible_quizzes"
            );
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to resend work aid notification.");
            throw err;
        }
    }

    public async GetIFrameAvailability(url: string): Promise<IframeApiModel> {
        try {
            const urlJson = JSON.stringify(url);
            const response = await this.cvApiService.post<IframeApiModel>(
                "/api/v1/iframe_availability", urlJson
            );

            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed to get iframe availability.");
            throw err;
        }
    }

    public changeMode(addMode: boolean = false, editMode: boolean = false, reuseMode: boolean = false){
        const newMode: AddEditArgs= {addMode: addMode, editMode: editMode, reuseMode: reuseMode}
        this.modes.next(newMode)
    }
}
